import { OlLayer } from "./ol-layer";
import VectorLayer from "ol/layer/Vector";
import Feature, { type FeatureLike } from "ol/Feature";
import type VectorSource from "ol/source/Vector";
import type { FlatStyleLike } from "ol/style/flat";
import type { StyleLike } from "ol/style/Style";
import { OlFeatureClickEvent } from "../ol-feature-click-event";
import type {
    OlMapContextMenuEventDetail,
    OlMapContextMenuEventType,
    OlMapPointerEventDetail,
    OlMapPointerEventType,
} from "../ol-map-events";

export abstract class OlVectorLayer extends OlLayer<VectorLayer> {
    protected createLayer(): VectorLayer {
        const source = this.createSource();
        const style = this.createStyle();

        return new VectorLayer({
            visible: this.visible,
            opacity: this.opacity,
            zIndex: this.zIndex,
            minZoom: this.minZoom,
            maxZoom: this.maxZoom,

            source,

            style,
        });
    }

    protected abstract createSource(): VectorSource;

    protected abstract createStyle(): StyleLike | FlatStyleLike | null | undefined

    public override getPointerEventTypes(): readonly OlMapPointerEventType[] {
        return ["click", "pointermove"];
    }

    public override getContextMenuEventTypes(): readonly OlMapContextMenuEventType[] {
        return ["contextmenu"];
    }

    protected isFeatureInteractive(_feature: FeatureLike): boolean {
        return false;
    }

    public override handlePointerEvent(detail: OlMapPointerEventDetail): void {
        if (!this.layer) {
            return;
        }

        const feature = detail.event.map.forEachFeatureAtPixel(
            detail.event.pixel,
            (candidate, layer) => layer === this.layer ? candidate : undefined,
            { hitTolerance: detail.hitTolerance },
        );

        if (!feature) {
            return;
        }

        if (detail.type === "pointermove") {
            if (this.isFeatureInteractive(feature)) {
                detail.event.map.getTargetElement().style.cursor = "pointer";
            }
            return;
        }

        const clusteredFeatures = feature instanceof Feature ? feature.get("features") : undefined;
        const features = Array.isArray(clusteredFeatures) ? clusteredFeatures : [feature];
        this.dispatchEvent(new OlFeatureClickEvent({ features, mapBrowserEvent: detail.event }));
    }

    public override handleContextMenuEvent(detail: OlMapContextMenuEventDetail): void {
        // Subclasses may override this to provide a layer-specific context menu.
    }

}
